import styled from 'styled-components';
import { ColorNeutralWhite, Spacing24, Spacing32, Spacing80, Spacing96 } from 'autogen/design-tokens/tokens';
import { Button } from 'components/Button';
import LinkActionButton from 'components/Button/LinkActionButton';
import { rem } from 'modules/helpers/style';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import type { EventName } from 'modules/tracking/GA4/types/EventName';
import { breakpoint, fonts, sizes } from 'variables';
import type { IAdvisorToolboxQuickView, IToolboxTheme } from '../shared/types';

const ScrollWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 100%;
    overflow: auto;
    overscroll-behavior: contain;
    padding: 0 ${Spacing24} ${Spacing96};

    ${breakpoint.up(sizes.md)} {
        flex-direction: row-reverse;
        align-items: center;
        padding: 0 ${Spacing80} ${Spacing80};
    }
`;

const Text = styled.div`
    ${breakpoint.up(sizes.md)} {
        flex: 1 1 auto;
    }
`;

const Headline = styled.h2<{ theme: IToolboxTheme }>`
    font-weight: bold;
    font-size: ${rem(24)};
    font-family: ${fonts.CustomFonts.DMSerifText};
    line-height: ${({ theme }) => (theme.inline ? 1 : 1.25)};
`;

const Description = styled.div`
    margin-bottom: ${Spacing24};
`;

const ImageWrapper = styled.div`
    flex: 0 0 auto;

    ${breakpoint.up(sizes.md)} {
        width: 300px;
        height: auto;
        margin-top: 0;
        margin-right: ${Spacing24};
    }
`;

const Image = styled.img`
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
`;

const CtaContainer = styled.div`
    position: fixed;
    left: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: ${Spacing80};
    background-color: ${ColorNeutralWhite};

    ${breakpoint.up(sizes.md)} {
        position: static;
        display: block;
        width: auto;
        height: auto;
        background-color: transparent;
    }
`;

const StyledLinkActionButton = styled(LinkActionButton)`
    width: calc(100% - ${Spacing32});

    ${breakpoint.up(sizes.md)} {
        display: inline-flex;
        width: auto;
        min-width: 120px;
    }
`;

const StyledButton = styled(Button)`
    width: calc(100% - ${Spacing32});

    ${breakpoint.up(sizes.md)} {
        display: inline-flex;
        width: auto;
        min-width: 120px;
    }
`;

export default function SlideView(props: { quickView: IAdvisorToolboxQuickView }) {
    const { quickView } = props;
    const { ctaText, ctaLinkAction, description, headline, imageUrl, type } = quickView;

    function handleTracking() {
        function getEventName(): EventName | undefined {
            switch (type) {
                case 'Booking':
                    return 'booking_activated';
                case 'Chat':
                    return 'chat_activated';
                case 'Questionnaire':
                    return 'questionnaire_activated';
                default:
                    return undefined;
            }
        }

        const eventName = getEventName();

        if (eventName) {
            GA4Tracking({
                eventName,
                context: 'advisor_toolbox',
                category: 'advisory'
            });
        }
    }

    return (
        <ScrollWrapper>
            <Text>
                <Headline>{headline}</Headline>
                <Description>{description}</Description>
                <CtaContainer>
                    {ctaLinkAction && (
                        <StyledLinkActionButton action={ctaLinkAction} onClick={handleTracking}>
                            {ctaText}
                        </StyledLinkActionButton>
                    )}
                </CtaContainer>
            </Text>
            <ImageWrapper>
                <Image src={imageUrl} alt={headline} />
            </ImageWrapper>
        </ScrollWrapper>
    );
}
